import Vue from 'vue';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from './';
import {
  FreightEntityConfig,
  OrderEntityConfig,
  PickupEntityConfig
} from '@/helpers/types/configs';
import {
  FilterOptions,
  applyValueLimitations,
  getDefaultEmptyValue,
  setDefaultFilter
} from '@/helpers/filter';
import { Counties } from '../../../shared/utils/enums';
import ApiService from '../services/ApiService';
import { capitalize } from '@/helpers/string';
import { OrderType } from '@/helpers/types/order';
import i18n from '@/locales/i18n';
import {
  translatedFreightStatuses,
  translatedOrderStatuses,
  translatedPickupStatuses
} from '../../../shared/utils/translations';
import { buildAbilityFor } from '@/services/ability';
import { keepKeys } from '@/helpers/object';

type CaslAbility = ReturnType<typeof buildAbilityFor>;

type DropdownItem = {
  text: string;
  value: string;
};

export type FilterSettings = {
  config?: FilterOptions[];
  filter?: Record<string, any>;
  items?: Record<string, DropdownItem[]>;
};

export type State = {
  order: FilterSettings;
  freight: FilterSettings;
  pickup: FilterSettings;
};

const emptyFieldFilterKeys = {
  MAIN_EXTERNAL_ID_PLACEHOLDER: 'showEmptyMainExternalId',
  expectedDeadlineDate: 'showEmptyExpectedDeadlineDate',
  firstReleaseDateEstimation: 'showEmptyReleaseDateEstimation',
  releaseDate: 'showEmptyReleaseDate',
  preferredTrucks: 'showEmptyVehicles',
  transporterName: 'showEmptyTransporterNames',
  timeSlotDock: 'showEmptyDocks',
  timeSlotTime: 'showEmptyTimeSlotTime'
};

const state: State = {
  order: {},
  freight: {},
  pickup: {}
};

export type FilterEntity = keyof State;

const actions: ActionTree<State, RootState> = {
  async initFilterForEntity(
    { commit, dispatch, rootGetters },
    {
      filterEntity,
      mainExternalIdObject,
      query,
      valueLimitations,
      ability
    }: {
      filterEntity: FilterEntity;
      mainExternalIdObject: { name: string; displayName: string };
      query?: Record<string, any>;
      valueLimitations?: Record<string, any>;
      ability?: CaslAbility;
    }
  ) {
    await dispatch(`set${capitalize(filterEntity)}DropdownItems`, { ability });
    commit('setFilterConfig', {
      filterEntity,
      filterConfig: rootGetters[`app/${filterEntity}EntitiesConfig`],
      mainExternalIdObject,
      ability
    });
    const entityIdKey = `${filterEntity}Id`;
    // get filter stored in local storage (if exists)
    const filterInLocalStorageString = localStorage.getItem(`${filterEntity}Filter`);
    dispatch('setEmptyFilter', filterEntity);

    // if query is provided, use query to set filter for entity id but ignore others
    if (query?.[entityIdKey]) {
      // do not save this filter in local storage
      // because it is only used for linking entites between views
      dispatch('updateFilterByKey', {
        filterEntity,
        key: entityIdKey,
        value: query[entityIdKey]
      });
    }
    // else if no query but filter in local storage exists, use it to set filter
    else if (filterInLocalStorageString !== null) {
      const filterInLocalStorage = JSON.parse(filterInLocalStorageString);

      /* 
        Use only those filter keys that are defined in filter config
        to prevent invalid filter keys from local storage.
      */
      const validatedFilterInLocalStorage = keepKeys(
        state[filterEntity].filter ?? {},
        filterInLocalStorage
      );
      Vue.set(state[filterEntity], 'filter', validatedFilterInLocalStorage);

      applyValueLimitations(state[filterEntity].filter!, valueLimitations);

      // no need to update local storerage here because we are just using
      // the existing filter in local storage without modification
    }
    // if neither query nor filter in local storage exists, set default filter
    else {
      const defaultFilters = rootGetters[`app/defaultFilters`][filterEntity];
      setDefaultFilter(state[filterEntity].filter!, defaultFilters, valueLimitations);
    }
  },

  async setOrderDropdownItems({ commit, rootGetters }) {
    const dropdownItem: Record<string, DropdownItem[]> = {};
    // set counties
    dropdownItem.county = Counties.map((county) => ({
      text: county,
      value: county
    }));
    // set customerNames
    const { data: customerNames } = await ApiService.getAllCustomerNames();
    dropdownItem.customerName = customerNames.map((name: string) => ({
      text: name,
      value: name
    }));
    // set factoryShortNames
    const { data: factories } = await ApiService.getFactories();
    dropdownItem.factoryShortName = factories.map((factory) => ({
      text: factory.shortName,
      value: factory.shortName
    }));
    // set preferredTrucks
    const { data: vehicles } = await ApiService.getVehicles();
    dropdownItem.preferredTrucks = vehicles.map((vehicle) => ({
      text: vehicle.vehicleId,
      value: vehicle.vehicleId
    }));
    // set productCategories
    const productCategory = rootGetters['app/productCategory'];
    dropdownItem.productCategory = productCategory.map((category) => ({
      text: category,
      value: category
    }));
    // set statuses
    dropdownItem.status = [...translatedOrderStatuses];
    // set types
    dropdownItem.type = Object.values(OrderType).map((orderType) => ({
      text: i18n.t(orderType) as string,
      value: orderType
    }));

    commit('setDropdownItems', { filterEntity: 'order', items: dropdownItem });
  },

  async setFreightDropdownItems(
    { commit, dispatch, rootGetters, rootState },
    { ability }: { ability: CaslAbility }
  ) {
    const dropdownItem: Record<string, DropdownItem[]> = {};

    // set statuses
    const validStatuses = rootState.freight.freightStatuses;
    let statuses = [...translatedFreightStatuses].filter((status) =>
      validStatuses.includes(status.value)
    );
    const isTransporter = rootGetters['account/isTransporter'];
    const freightStatus = rootState.app.enums.freightStatus;
    if (isTransporter) {
      statuses = statuses.filter(
        (status) =>
          Object.values(freightStatus).indexOf(status.value) >=
          Object.values(freightStatus).indexOf(freightStatus.accepted)
      );
    }
    dropdownItem.status = statuses;

    // set transporters
    if (ability.can('filterTransporters', 'Freights')) {
      await dispatch('transporters/getAll', null, { root: true });
      dropdownItem.transporterName = rootGetters['transporters/transportersCompanyNames'];
    }

    // set factories
    await dispatch('factories/get', null, { root: true });
    dropdownItem.factoryShortName = rootGetters['factories/factoryShortNames'];
    dropdownItem.factoryNamesWithShortNames =
      rootGetters['factories/sortedFactoryNamesWithShortNames'];

    // set depots
    await dispatch('depots/get', null, { root: true });
    dropdownItem.depotShortName = rootGetters['depots/sortedDepotNamesWithShortNames'];

    // set vehicles
    await dispatch('vehicles/get', null, { root: true });
    dropdownItem.truckType = rootGetters['vehicles/vehicleIds'];

    // set counties
    dropdownItem.county = Counties.map((county) => ({
      text: county,
      value: county
    }));

    // set docks
    const isTimeSlotModuleActive = rootGetters['module/isTimeSlotModuleActive'];
    if (isTimeSlotModuleActive) {
      dropdownItem.timeSlotDock = rootGetters['depots/getDocksWithDepotShortNames'].map((dock) => ({
        text: `${dock.name} (${dock.depotShortName})`,
        value: dock.id
      }));
    }

    commit('setDropdownItems', { filterEntity: 'freight', items: dropdownItem });
  },

  async setPickupDropdownItems({ commit, dispatch, rootGetters, rootState }) {
    const dropdownItem: Record<string, DropdownItem[]> = {};

    // set statuses
    const validStatuses = rootState.pickups.pickupStatuses;
    dropdownItem.status = [...translatedPickupStatuses].filter((status) =>
      validStatuses.includes(status.value)
    );

    // set depots
    await dispatch('depots/get', null, { root: true });
    const isTimeSlotModuleActive = rootGetters['module/isTimeSlotModuleActive'];
    if (isTimeSlotModuleActive) {
      dropdownItem.timeSlotDock = rootGetters['depots/getDocksWithDepotShortNames'].map((dock) => ({
        text: `${dock.name} (${dock.depotShortName})`,
        value: dock.id
      }));
    }

    commit('setDropdownItems', { filterEntity: 'pickup', items: dropdownItem });
  },

  updateFilterByKey(
    { commit },
    payload: { filterEntity: FilterEntity; key: string; value: any; externalIdKey?: string }
  ) {
    commit('updateFilterByKey', payload);
  },

  setEmptyFilter({ commit }, filterEntity: FilterEntity) {
    commit('setEmptyFilter', filterEntity);
  },

  unsetFilter({ commit }, filterEntity: FilterEntity) {
    commit('unsetFilter', filterEntity);
  },

  getFilterConfig({ state }, filterEntity: FilterEntity): FilterOptions[] {
    return state[filterEntity].config as FilterOptions[];
  },

  saveToLocalStorage({ state }, payload: { filterEntity: FilterEntity }) {
    localStorage.setItem(
      `${payload.filterEntity}Filter`,
      JSON.stringify(state[payload.filterEntity].filter)
    );
  }
};

const mutations: MutationTree<State> = {
  updateFilterByKey(
    state,
    {
      filterEntity,
      key,
      value,
      externalIdKey
    }: { filterEntity: FilterEntity; key: string; value: any; externalIdKey?: string }
  ) {
    if (externalIdKey) {
      if (!state[filterEntity].filter![externalIdKey]) {
        Vue.set(state[filterEntity].filter!, externalIdKey, {});
      }
      if (value === null || value === undefined || value === '') {
        Vue.delete(state[filterEntity].filter![externalIdKey], key);
        return;
      }
      Vue.set(state[filterEntity].filter![externalIdKey], key, value);
      return;
    }
    if (value === null || value === undefined || value === '') {
      Vue.delete(state[filterEntity].filter!, key);
      return;
    }
    Vue.set(state[filterEntity].filter!, key, value);
  },

  setEmptyFilter(state, filterEntity: FilterEntity) {
    Vue.set(state[filterEntity], 'filter', {});
    const filterConfig = (state[filterEntity].config ?? []) as FilterOptions[];
    for (const configItem of filterConfig) {
      // set filter for main external id
      if (configItem.label === 'MAIN_EXTERNAL_ID_PLACEHOLDER') {
        Vue.set(state[filterEntity].filter!, 'mainExternalId', '');
      }
      // set filter for externalIds
      else if (configItem.value.startsWith('externalIds.') && configItem.entityType) {
        if (!state[filterEntity].filter![`${configItem.entityType}ExternalIds`]) {
          Vue.set(state[filterEntity].filter!, `${configItem.entityType}ExternalIds`, {});
        }
      }
      // set empty value for other filters
      else {
        Vue.set(
          state[filterEntity].filter!,
          configItem.value,
          configItem.emptyValue ?? getDefaultEmptyValue(configItem.type)
        );
      }

      // set empty field filters
      if (emptyFieldFilterKeys[configItem.value]) {
        Vue.set(state[filterEntity].filter!, emptyFieldFilterKeys[configItem.value], false);
      }
    }
  },

  setFilterConfig(
    state,
    {
      filterEntity,
      filterConfig,
      mainExternalIdObject
    }: {
      filterEntity: FilterEntity;
      filterConfig: OrderEntityConfig[] | FreightEntityConfig[] | PickupEntityConfig[];
      mainExternalIdObject: { name: string; displayName: string };
    }
  ) {
    const filterOptions = filterConfig
      .filter((configItem) => !!configItem.filter)
      .sort((prev, curr) => prev.filter!.position - curr.filter!.position)
      .map((configItem) => {
        const filterOption: FilterOptions = {
          type: configItem.filter!.type,
          value: configItem.filter?.value ?? configItem.value,
          label: configItem.filter?.label ?? configItem.text,
          cols: configItem.filter?.cols,
          md: configItem.filter?.md,
          xl: configItem.filter?.xl,
          emptyValue:
            configItem.filter?.emptyValue ?? getDefaultEmptyValue(configItem.filter!.type),
          placeholder: configItem.filter?.placeholder,
          items: state[filterEntity].items![configItem.value],
          emptyCheckboxName: configItem.filter?.filterForEmptyValues
            ? emptyFieldFilterKeys[configItem.value]
            : undefined,
          maxItemsToShow: configItem.filter?.maxItemsToShow,
          entityType: configItem.filter?.entityType,
          isMain: configItem.filter?.isMain,
          splitPosition: configItem.filter?.splitPosition ?? configItem.filter?.position,
          splitSize: {
            cols: configItem.filter?.splitSize?.cols ?? configItem.filter?.cols,
            md: configItem.filter?.splitSize?.md ?? configItem.filter?.md,
            xl: configItem.filter?.splitSize?.xl ?? configItem.filter?.xl
          },
          showIf: configItem.filter?.showIf
        };

        // set main external id in filter
        if (configItem.text === 'MAIN_EXTERNAL_ID_PLACEHOLDER') {
          if (mainExternalIdObject?.name) {
            filterOption.label = mainExternalIdObject.displayName;
            filterOption.value = `mainExternalId`;
            filterOption.emptyValue = '';
          } else {
            return undefined;
          }
        }

        return filterOption;
      })
      .filter((configItem) => configItem !== undefined);
    Vue.set(state[filterEntity], 'config', filterOptions);
  },

  setDropdownItems(state, { filterEntity, items }) {
    Vue.set(state[filterEntity], 'items', items);
  },

  unsetFilter(state, filterEntity: FilterEntity) {
    Vue.set(state, filterEntity, {});
  }
};

const getters: GetterTree<State, RootState> = {
  getFilter: (state) => (filterEntity: FilterEntity) => state[filterEntity].filter,

  getFilterConfig: (state) => (filterEntity: FilterEntity) => state[filterEntity].config
};

export const filter = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
